import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { timeAgo, STATUSES, CATEGORIES, ISSUE_TYPES } from '../../utils/helpers';
import { exportToExcel } from '../../utils/exportExcel';
import { useAuth } from '../../context/AuthContext';
import { useFeatures } from '../../utils/useFeatures';
import useIsMobile from '../../utils/useIsMobile';

const PRIORITIES = ['low', 'medium', 'high', 'critical'];

export default function AdminTickets() {
  const location = useLocation();
  const basePath = location.pathname.startsWith('/manager') ? '/manager/tickets' : '/admin/tickets';
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [data, setData] = useState({ tickets: [], hasMore: false });
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [clients, setClients] = useState([]);
  const [clientNames, setClientNames] = useState([]);
  const { tenant } = useFeatures();
  const singleTenant = !!(tenant?.enabled && tenant?.customer_id);
  const [agentOptions, setAgentOptions] = useState([]);
  const [filters, setFilters] = useState({ status: '', client: '', agent: '', category: '', search: '', date_from: '', date_to: '', page: 1, sort: '', dir: 'desc' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [showCreate, setShowCreate] = useState(false);
  const [showForward, setShowForward] = useState(null); // ticket object
  const nav = useNavigate();
  const searchRef = useRef(null);

  useEffect(() => {
    apiFetch('/customers?limit=1000').then(d => setClients(d.customers || [])).catch(() => {});
    apiFetch('/tickets/clients').then(d => setClientNames(d || [])).catch(() => {});
    apiFetch('/users/agents').then(d => setAgentOptions((d || []).filter(a => ['agent', 'manager', 'admin'].includes(a.role)))).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search), 350);
    return () => clearTimeout(t);
  }, [filters.search]);

  useEffect(() => {
    setLoading(true);
    setFetchError('');
    const active = { ...filters, search: debouncedSearch };
    const params = new URLSearchParams(Object.fromEntries(Object.entries(active).filter(([,v]) => v)));
    apiFetch(`/tickets?${params}&limit=25`)
      .then(setData)
      .catch(err => setFetchError(err.message))
      .finally(() => setLoading(false));
  }, [filters.status, filters.client, filters.agent, filters.date_from, filters.date_to, filters.page, filters.sort, filters.dir, debouncedSearch]);

  function toggleSort(col) {
    if (!col) return;
    setFilters(f => f.sort === col
      ? { ...f, dir: f.dir === 'asc' ? 'desc' : 'asc', page: 1 }
      : { ...f, sort: col, dir: 'asc', page: 1 });
  }

  function setFilter(key, value) {
    setFilters(f => ({ ...f, [key]: value, page: 1 }));
    setSelectedIdx(-1);
  }

  function refreshTickets() {
    const active = { ...filters, search: debouncedSearch };
    const params = new URLSearchParams(Object.fromEntries(Object.entries(active).filter(([,v]) => v)));
    apiFetch(`/tickets?${params}&limit=25`).then(setData).catch(() => {});
  }

  const handleKeyNav = useCallback((e) => {
    const tickets = data.tickets;
    if (!tickets.length) return;
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' && document.activeElement !== searchRef.current) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, tickets.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIdx >= 0 && tickets[selectedIdx]) {
        nav(`${basePath}/${tickets[selectedIdx].id}`);
      }
    }
  }, [data.tickets, selectedIdx, nav, basePath]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyNav);
    return () => window.removeEventListener('keydown', handleKeyNav);
  }, [handleKeyNav]);

  async function handleExport() {
    setExporting(true);
    try {
      const params = new URLSearchParams(
        Object.fromEntries(Object.entries(filters).filter(([k, v]) => v && k !== 'page'))
      );
      const all = await apiFetch(`/tickets?${params}&limit=5000`);
      const rows = all.tickets || [];
      exportToExcel(rows, [
        { header: 'Ticket #',      key: 'ticket_number' },
        { header: 'Title',         key: 'title' },
        { header: 'Customer',      key: 'customer_name' },
        { header: 'Location',      key: 'location' },
        { header: 'Product',       key: 'product_name' },
        { header: 'Serial No.',    key: 'serial_number' },
        { header: 'Issue Type',    key: 'issue_type' },
        { header: 'Category',      key: 'category' },
        { header: 'Priority',      key: 'priority' },
        { header: 'Status',        key: 'status' },
        { header: 'Contact',       key: 'contact_name' },
        { header: 'Contact Mobile', key: 'contact_mobile' },
        { header: 'Started On',    key: 'started_on' },
        { header: 'Recurring',     key: 'is_recurring' },
        { header: 'Assigned To',   key: 'agent_name' },
        { header: 'Agent Tier',    key: 'agent_tier' },
        { header: 'SLA Breached',  key: 'sla_breached' },
        { header: 'Due Date',      key: 'due_at' },
        { header: 'Close Reason',  key: 'close_reason' },
        { header: 'Close Remark',  key: 'close_remark' },
        { header: 'Created',       key: 'created_at' },
        { header: 'Updated',       key: 'updated_at' },
        { header: 'Resolved At',   key: 'resolved_at' },
        { header: 'Closed At',     key: 'closed_at' },
      ], `tickets-export-${new Date().toISOString().slice(0,10)}.xlsx`);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">All Tickets</h1>
          <p className="page-sub">{data.tickets?.length ? `Page ${filters.page}${data.hasMore ? ' — more available' : ''}` : 'No tickets'}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={handleExport} disabled={exporting || !data.tickets?.length} style={{ fontSize: 13 }}>
            {exporting ? '⏳ Exporting…' : '📊 Export to Excel'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)} style={{ fontSize: 13 }}>
            + New Ticket
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <input ref={searchRef} className="input" style={{ maxWidth: 240, flex: '1 1 180px' }} placeholder="🔍  Search tickets…" value={filters.search}
          onChange={e => setFilter('search', e.target.value)} />
        <select className="input" style={{ maxWidth: 150 }} value={filters.status} onChange={e => setFilter('status', e.target.value)}>
          <option value="">All Status</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
        </select>
        {!singleTenant && (
        <select className="input" style={{ maxWidth: 180 }} value={filters.client} onChange={e => setFilter('client', e.target.value)}>
          <option value="">All Clients</option>
          {clientNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        )}
        <select className="input" style={{ maxWidth: 180 }} value={filters.agent} onChange={e => setFilter('agent', e.target.value)}>
          <option value="">All Agents</option>
          <option value="unassigned">⚠ Unassigned</option>
          {agentOptions.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>From</span>
          <input type="date" className="input" style={{ maxWidth: 150 }} value={filters.date_from}
            onChange={e => {
              setFilter('date_from', e.target.value);
              if (!filters.date_to) setFilter('date_to', new Date().toISOString().slice(0, 10));
            }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>To</span>
          <input type="date" className="input" style={{ maxWidth: 150 }} value={filters.date_to}
            onChange={e => setFilter('date_to', e.target.value)} />
        </div>
        {(filters.date_from || filters.date_to) && (
          <button className="btn btn-secondary btn-sm" onClick={() => { setFilter('date_from', ''); setFilter('date_to', ''); }}>
            ✕ Clear dates
          </button>
        )}
      </div>

      {fetchError && (
        <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          Error loading tickets: {fetchError}
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><span className="spinner" /></div>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.tickets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>No tickets found.</div>
          ) : data.tickets.map(t => {
            const breached = t.sla_breached;
            const canForward = ['admin', 'manager', 'agent'].includes(user?.role) && !['resolved','closed'].includes(t.status);
            return (
              <div key={t.id} className="card" style={{ padding: 14, borderLeft: breached ? '3px solid #dc2626' : '3px solid transparent' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                  <Link to={`${basePath}/${t.id}`} style={{ fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none' }}>{t.title}</Link>
                  <span className={`badge badge-${t.status}`} style={{ flexShrink: 0 }}>{t.status.replace('_',' ')}</span>
                </div>
                <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#94a3b8', marginTop: 3 }}>{t.ticket_number}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 6 }}>
                  {t.company_name || t.customer_name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <span className={`badge badge-${t.priority}`}>{t.priority}</span>
                  <span style={{ fontSize: 12, color: t.agent_name ? 'var(--text-secondary)' : '#f59e0b', fontWeight: 500 }}>{t.agent_name || 'Unassigned'}</span>
                  <span style={{ fontSize: 12, fontWeight: breached ? 700 : 400, color: breached ? '#b91c1c' : 'var(--text-secondary)', marginLeft: 'auto' }}>
                    {breached ? '⚠ Breached' : (t.due_at ? new Date(t.due_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—')}
                  </span>
                </div>
                {canForward && (
                  <button onClick={() => setShowForward(t)} style={{ marginTop: 10, fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #bfdbfe', background: 'var(--blue-50)', color: 'var(--blue-700)', fontWeight: 600, cursor: 'pointer' }}>↗ Forward</button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="table-wrap">
          <table className="kg-table">
            <thead>
              <tr>
                {[
                  ['Ticket #', 'ticket_number'], ['Title', 'title'], ['Customer', 'customer'],
                  ['Location', 'location'],
                  ['Assigned / Tier', 'agent'], ['Priority', 'priority'], ['Status', 'status'],
                  ['SLA / Due', 'due'], ['Updated', 'updated'], ['', null],
                ].map(([h, col]) => {
                  const active = col && filters.sort === col;
                  return (
                    <th key={h || 'actions'} onClick={() => toggleSort(col)}
                      style={{ cursor: col ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}
                      title={col ? 'Sort' : undefined}>
                      {h}
                      {col && (
                        <span style={{ marginLeft: 5, fontSize: 11, color: active ? '#0B6623' : 'var(--text-secondary)', opacity: active ? 1 : 0.4 }}>
                          {active ? (filters.dir === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.tickets.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <div className="empty-state">
                      <div className="empty-state-icon">🎫</div>
                      <div className="empty-state-title">No tickets found</div>
                      <div className="empty-state-sub">Try adjusting your filters</div>
                    </div>
                  </td>
                </tr>
              ) : data.tickets.map((t, idx) => {
                const breached = t.sla_breached;
                const isSelected = idx === selectedIdx;
                const canForward = ['admin', 'manager', 'agent'].includes(user?.role) && !['resolved','closed'].includes(t.status);
                return (
                  <tr key={t.id} className={breached ? 'sla-breached' : ''}
                    style={isSelected ? { background: 'var(--action-bg)', outline: '2px solid #0B6623', outlineOffset: -2 } : {}}
                    onClick={() => setSelectedIdx(idx)}>
                    <td>
                      <Link to={`${basePath}/${t.id}`}>
                        <span className="ticket-chip">{t.ticket_number}</span>
                      </Link>
                      {t.created_by_staff && (
                        <div style={{ fontSize: 10, color: '#0284c7', marginTop: 2, fontWeight: 600 }}>📞 On behalf</div>
                      )}
                    </td>
                    <td style={{ maxWidth: 240 }}>
                      <Link to={`${basePath}/${t.id}`} style={{ fontWeight: 600, color: 'var(--text-main)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.title}
                      </Link>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {t.company_name && <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{t.company_name}</div>}
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.customer_name}</div>
                    </td>
                    <td style={{ fontSize: 12.5, color: t.location ? 'var(--text-primary)' : 'var(--text-secondary)', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.location || ''}>
                      {t.location ? `📍 ${t.location}` : '—'}
                    </td>
                    <td>
                      {t.agent_name
                        ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>{t.agent_name}</span>
                            {t.agent_tier && <TierBadge tier={t.agent_tier} />}
                          </div>
                        : <span style={{ color: '#f59e0b', fontSize: 12, fontWeight: 500 }}>Unassigned</span>
                      }
                    </td>
                    <td><span className={`badge badge-${t.priority}`}>{t.priority}</span></td>
                    <td><span className={`badge badge-${t.status}`}>{t.status.replace('_',' ')}</span></td>
                    <td style={{ fontSize: 12.5, color: breached ? '#b91c1c' : 'var(--text-secondary)', fontWeight: breached ? 700 : 400 }}>
                      {breached ? '⚠ Breached' : (t.due_at ? new Date(t.due_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—')}
                    </td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{timeAgo(t.updated_at)}</td>
                    <td onClick={e => e.stopPropagation()}>
                      {canForward && (
                        <button onClick={() => setShowForward(t)}
                          style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid #bfdbfe', background: 'var(--blue-50)', color: 'var(--blue-700)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          ↗ Forward
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(filters.page > 1 || data.hasMore) && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <button className="btn btn-secondary btn-sm" disabled={filters.page <= 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>← Prev</button>
          <span style={{ padding: '5px 10px', fontSize: 13 }}>Page {filters.page}</span>
          <button className="btn btn-secondary btn-sm" disabled={!data.hasMore} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Next →</button>
        </div>
      )}

      {showCreate && (
        <CreateTicketModal
          clients={clients}
          tenantId={singleTenant ? tenant.customer_id : ''}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refreshTickets(); }}
        />
      )}

      {showForward && (
        <ForwardModal
          ticket={showForward}
          onClose={() => setShowForward(null)}
          onDone={() => { setShowForward(null); refreshTickets(); }}
        />
      )}
    </div>
  );
}

function ClientSearch({ clients, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hiIdx, setHiIdx] = useState(0);
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selected = clients.find(c => c.id === value);
  const filtered = query.trim()
    ? clients.filter(c => c.name.toLowerCase().includes(query.toLowerCase()))
    : clients;

  useEffect(() => { setHiIdx(0); }, [query]);

  useEffect(() => {
    function onClickOut(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  function select(c) {
    onChange(c.id);
    setQuery('');
    setOpen(false);
  }

  function handleKey(e) {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHiIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHiIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[hiIdx]) select(filtered[hiIdx]); }
    else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {selected && !open ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1.5px solid #0B6623', borderRadius: 8, background: 'var(--action-bg)', cursor: 'pointer' }}
          onClick={() => { setOpen(true); setQuery(''); setTimeout(() => inputRef.current?.focus(), 0); }}>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            {selected.name}{selected.product ? ` — ${selected.product}` : ''}
          </span>
          <button type="button" onClick={e => { e.stopPropagation(); onChange(''); setQuery(''); }}
            style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text-secondary)', lineHeight: 1, padding: 0 }}>×</button>
        </div>
      ) : (
        <input
          ref={inputRef}
          className="input"
          autoFocus={open}
          placeholder="Search client name…"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
        />
      )}
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: 'var(--bg-surface)', border: '1.5px solid var(--border-subtle)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>No clients found</div>
          ) : filtered.map((c, i) => (
            <div key={c.id} onMouseDown={() => select(c)}
              style={{ padding: '9px 14px', fontSize: 13, cursor: 'pointer', background: i === hiIdx ? 'var(--action-bg)' : 'transparent', borderLeft: i === hiIdx ? '3px solid #0B6623' : '3px solid transparent', fontWeight: i === hiIdx ? 600 : 400 }}
              onMouseEnter={() => setHiIdx(i)}>
              <span style={{ color: 'var(--text-primary)' }}>{c.name}</span>
              {c.product && <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 6 }}>{c.product}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateTicketModal({ clients, tenantId = '', onClose, onCreated }) {
  const [form, setForm] = useState({ on_behalf_of: tenantId || '', title: '', description: '', priority: 'medium', category: 'general', product_name: '', serial_number: '', issue_type: '', location: '', contact_name: '', contact_mobile: '', started_on: '', is_recurring: 0 });
  const [allProducts, setAllProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/products').then(d => setAllProducts(d.products || d || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.on_behalf_of) { setForm(f => ({ ...f, product_name: '', serial_number: '' })); return; }
    const client = clients.find(c => c.id === form.on_behalf_of);
    // Pre-select product if already set on customer record
    setForm(f => ({ ...f, product_name: client?.product || '', serial_number: client?.serial_number || '' }));
  }, [form.on_behalf_of, clients]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.on_behalf_of) return setError('Please select a client');
    if (!form.title.trim()) return setError('Title is required');
    if (!form.description.trim()) return setError('Description is required');
    setSaving(true); setError('');
    try {
      await apiFetch('/tickets', { method: 'POST', body: form });
      onCreated();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  }

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,.2)', maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Create Ticket on Behalf of Client</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
        </div>

        {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {!tenantId && (
            <div>
              <label style={labelStyle}>Client *</label>
              <ClientSearch clients={clients} value={form.on_behalf_of} onChange={val => f('on_behalf_of', val)} />
            </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Priority</label>
                <select className="input" value={form.priority} onChange={e => f('priority', e.target.value)}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Category</label>
                <select className="input" value={form.category} onChange={e => f('category', e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_',' ')}</option>)}
                </select>
              </div>
            </div>

            {form.on_behalf_of && (
              <div>
                <label style={labelStyle}>Product *</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {allProducts.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => setForm(f => ({ ...f, product_name: p.name }))}
                      style={{
                        padding: '6px 14px', borderRadius: 20, border: '1.5px solid',
                        borderColor: form.product_name === p.name ? '#0B6623' : 'var(--border-subtle)',
                        background: form.product_name === p.name ? '#0B6623' : 'var(--bg-surface)',
                        color: form.product_name === p.name ? '#fff' : 'var(--text-primary)',
                        fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all .12s',
                      }}>
                      {p.name}
                    </button>
                  ))}
                </div>
                {form.product_name && (
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
                    Selected: <strong style={{ color: '#0B6623' }}>{form.product_name}</strong>
                    {form.serial_number ? ` — S/N: ${form.serial_number}` : ''}
                    {' · '}
                    <span style={{ cursor: 'pointer', color: '#dc2626' }} onClick={() => setForm(f => ({ ...f, product_name: '' }))}>clear</span>
                  </div>
                )}
              </div>
            )}

            <div>
              <label style={labelStyle}>Title *</label>
              <input className="input" value={form.title} onChange={e => f('title', e.target.value)} placeholder="Brief issue summary" maxLength={255} required />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Issue type</label>
                <select className="input" value={form.issue_type} onChange={e => f('issue_type', e.target.value)}>
                  <option value="">Select…</option>
                  {ISSUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Location / Branch</label>
                <input className="input" value={form.location} onChange={e => f('location', e.target.value)} placeholder="Branch / site" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Contact person</label>
                <input className="input" value={form.contact_name} onChange={e => f('contact_name', e.target.value)} placeholder="Who to reach" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Contact mobile</label>
                <input className="input" value={form.contact_mobile} onChange={e => f('contact_mobile', e.target.value)} placeholder="10-digit mobile" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Started on</label>
                <input className="input" type="date" value={form.started_on} onChange={e => f('started_on', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Recurring?</label>
                <select className="input" value={form.is_recurring} onChange={e => f('is_recurring', Number(e.target.value))}>
                  <option value={0}>No — first time</option>
                  <option value={1}>Yes — repeatedly</option>
                </select>
              </div>
            </div>

            <div>
              <label style={labelStyle}>Description *</label>
              <textarea className="input" value={form.description} onChange={e => f('description', e.target.value)}
                placeholder="Describe the issue reported by the client…" rows={5} required
                style={{ resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={cancelBtnStyle}>Cancel</button>
            <button type="submit" disabled={saving} style={submitBtnStyle}>{saving ? 'Creating…' : 'Create Ticket'}</button>
          </div>
        </form>

        <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 12, marginBottom: 0 }}>
          Ticket will be assigned to you. You can forward it to a manager after creation.
        </p>
      </div>
    </div>
  );
}

function ForwardModal({ ticket, onClose, onDone }) {
  const [managers, setManagers] = useState([]);
  const [managerId, setManagerId] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/users/agents').then(d => setManagers((d || []).filter(u => ['manager','admin'].includes(u.role)))).catch(() => {});
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await apiFetch(`/tickets/${ticket.id}/forward`, { method: 'POST', body: { manager_id: managerId || undefined, note } });
      onDone();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Forward Ticket to Manager</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
        </div>

        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8 }}>
          <strong style={{ color: 'var(--text-primary)' }}>{ticket.ticket_number}</strong> — {ticket.title}
        </div>

        {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>Forward To</label>
              <select className="input" value={managerId} onChange={e => setManagerId(e.target.value)}>
                <option value="">— Auto-assign to available manager —</option>
                {managers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Note (optional)</label>
              <textarea className="input" value={note} onChange={e => setNote(e.target.value)}
                placeholder="Reason for forwarding or instructions for manager…" rows={3}
                style={{ resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={cancelBtnStyle}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...submitBtnStyle, background: '#0284c7' }}>{saving ? 'Forwarding…' : '↗ Forward to Manager'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TierBadge({ tier }) {
  const s = {
    L1: { bg: 'var(--action-bg)', color: 'var(--status-resolved-text)', border: 'var(--action-hover)' },
    L2: { bg: 'var(--blue-100)', color: 'var(--blue-700)', border: 'var(--blue-200)' },
    L3: { bg: '#ede9fe', color: '#6d28d9', border: '#c4b5fd' },
    Other: { bg: 'var(--n-100)', color: 'var(--text-muted)', border: 'var(--border-subtle)' },
  }[tier] || { bg: 'var(--n-100)', color: 'var(--text-muted)', border: 'var(--border-subtle)' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 999, padding: '1px 6px', letterSpacing: .3 }}>
      {tier}
    </span>
  );
}

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 5 };
const cancelBtnStyle = { flex: 1, padding: 10, borderRadius: 8, border: '1.5px solid var(--border-subtle)', background: 'var(--bg-surface)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' };
const submitBtnStyle = { flex: 1, padding: 10, borderRadius: 8, border: 'none', background: '#0B6623', fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer' };
